import { ErrandTypeRelativeToVisitEnum } from "~/graphql/__generated__/graphql";
import type { CareRequirementProximityConstraint, DriveTimeOptimizerArrivalWindow } from "./driveTimeOptimizerConversion";

export const ERRAND_PRECEDENCE_WINDOW_MINUTES = 120;

export function sortVisitResourcesByStartTime<T extends { startTime: string }>(resources: T[]): T[] {
  return [...resources].sort(
    (left, right) => new Date(left.startTime).getTime() - new Date(right.startTime).getTime()
  );
}

export function getPrecedingResourceId(
  resourceId: number,
  sortedOptimizableChain: { id: number }[],
): number | undefined {
  const index = sortedOptimizableChain.findIndex((resource) => resource.id === resourceId);
  if (index <= 0) return undefined;
  return sortedOptimizableChain[index - 1]?.id;
}

function toWholeMinute(timestamp: string): number {
  return Math.round(new Date(timestamp).getTime() / 60_000);
}

// Starts are rounded before subtracting so that A->B and B->A offsets are exact negatives;
// rounding the difference instead is asymmetric at half minutes (Math.round(2.5) = 3, Math.round(-2.5) = -2).
export function getNonErrandSiblingOffsets(
  resource: { id: number; startTime: string },
  otherResources: { id: number; startTime: string; visitResourceRequirementId?: number | null }[],
  resourceIsErrand: boolean,
  otherIsErrand: (other: { id: number; visitResourceRequirementId?: number | null }) => boolean,
): { visitResourceId: number; offsetMinutes: number }[] {
  if (resourceIsErrand) return [];
  const resourceStartMinute = toWholeMinute(resource.startTime);
  return otherResources
    .filter((other) => !otherIsErrand(other))
    .map((other) => ({
      visitResourceId: other.id,
      offsetMinutes: toWholeMinute(other.startTime) - resourceStartMinute,
    }));
}

// The purpose of this function is to ensure that the relativeToVisit is always one of the two valid values.
// If we add a new value to the enum, this function will throw an error until we handle the new value.
export function getErrandRelativeToVisit(
  relativeToVisit: ErrandTypeRelativeToVisitEnum,
): ErrandTypeRelativeToVisitEnum {
  switch (relativeToVisit) {
    case ErrandTypeRelativeToVisitEnum.After:
      return ErrandTypeRelativeToVisitEnum.After;
    case ErrandTypeRelativeToVisitEnum.Before:
      return ErrandTypeRelativeToVisitEnum.Before;
    default: {
      const exhaustive: never = relativeToVisit;
      return exhaustive;
    }
  }
}

export function durationMinutes(startTime: string, endTime: string): number {
  return Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60_000);
}

export function getErrandProximityConstraint(
  errandResourceId: number,
  errandDurationMinutes: number,
  anchorResourceId: number,
  anchorDurationMinutes: number,
  relativeToVisit: ErrandTypeRelativeToVisitEnum,
): CareRequirementProximityConstraint {
  const relative = getErrandRelativeToVisit(relativeToVisit);
  switch (relative) {
    case ErrandTypeRelativeToVisitEnum.After:
      return {
        earlierVisitResourceId: anchorResourceId,
        laterVisitResourceId: errandResourceId,
        minProximityMinutes: anchorDurationMinutes,
        maxProximityMinutes: anchorDurationMinutes + ERRAND_PRECEDENCE_WINDOW_MINUTES,
      };
    case ErrandTypeRelativeToVisitEnum.Before:
      return {
        earlierVisitResourceId: errandResourceId,
        laterVisitResourceId: anchorResourceId,
        minProximityMinutes: errandDurationMinutes,
        maxProximityMinutes: errandDurationMinutes + ERRAND_PRECEDENCE_WINDOW_MINUTES,
      };
    default: {
      const exhaustive: never = relative;
      return exhaustive;
    }
  }
}

export function getConstrainedErrandArrivalBand(
  relativeToVisit: ErrandTypeRelativeToVisitEnum,
  anchorArrivalWindow: DriveTimeOptimizerArrivalWindow,
  anchorDurationMinutes: number,
  errandDurationMinutes: number,
): DriveTimeOptimizerArrivalWindow {
  const windowMs = ERRAND_PRECEDENCE_WINDOW_MINUTES * 60_000;
  const anchorDurationMs = anchorDurationMinutes * 60_000;
  const errandDurationMs = errandDurationMinutes * 60_000;
  const anchorArrivalStart = new Date(anchorArrivalWindow.arrivalWindowStart);
  const anchorArrivalEnd = new Date(anchorArrivalWindow.arrivalWindowEnd);
  const relative = getErrandRelativeToVisit(relativeToVisit);
  switch (relative) {
    case ErrandTypeRelativeToVisitEnum.After: {
      const bandStart = new Date(anchorArrivalStart.getTime() + anchorDurationMs);
      const bandEnd = new Date(anchorArrivalEnd.getTime() + anchorDurationMs + windowMs);
      return {
        arrivalWindowStart: bandStart.toISOString(),
        arrivalWindowEnd: bandEnd.toISOString(),
      };
    }
    case ErrandTypeRelativeToVisitEnum.Before: {
      const bandStart = new Date(anchorArrivalStart.getTime() - errandDurationMs - windowMs);
      const bandEnd = new Date(anchorArrivalEnd.getTime() - errandDurationMs);
      return {
        arrivalWindowStart: bandStart.toISOString(),
        arrivalWindowEnd: bandEnd.toISOString(),
      };
    }
    default: {
      const exhaustive: never = relative;
      return exhaustive;
    }
  }
}

export function getErrandAnchorResource<
  T extends { id: number; visitId: number; providerId: number | null; startTime: string },
>(
  errand: T,
  visitResources: T[],
  isErrand: (resource: T) => boolean,
): T | undefined {
  const nonErrands = visitResources.filter((resource) => resource.visitId === errand.visitId && !isErrand(resource));
  if (nonErrands.length === 0) return undefined;

  const sameProvider = errand.providerId != null
    ? nonErrands.filter((resource) => resource.providerId === errand.providerId)
    : [];
  const candidates = sameProvider.length > 0 ? sameProvider : nonErrands;
  return [...candidates].sort(
    (left, right) => new Date(left.startTime).getTime() - new Date(right.startTime).getTime()
  )[0];
}

export function isErrandVisitResource(
  resource: { visitResourceRequirementId?: number | null },
  errandTypeByRequirementId: Record<string | number, unknown>,
): boolean {
  if (resource.visitResourceRequirementId == null) return false;
  return errandTypeByRequirementId[resource.visitResourceRequirementId] != null;
}
